import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';

// Centro de alertas de la empresa (#89): solo lo pendiente, cada aviso con
// enlace directo al caso. Se calcula al vuelo (sin tabla propia) con lo que el
// Dashboard ya trae (cuentas, tasa del día) más las facturas de compra
// abiertas (/compras/cxp; si el usuario no tiene acceso a compras, esas
// alertas simplemente no aparecen) y el flujo proyectado (#86).
const DIAS_SIN_MOVIMIENTO = 15;

function diasDesde(fecha, hoy) {
  return Math.round((new Date(`${hoy}T00:00:00`) - new Date(`${String(fecha).slice(0, 10)}T00:00:00`)) / 86400000);
}

export default function TarjetaAlertas({ cuentas, tasasHoy, empresaActiva, altoLibre = false }) {
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState([]);
  const [proyectado, setProyectado] = useState(null);

  useEffect(() => {
    if (!empresaActiva) return;
    let vigente = true;
    api.get('/compras/cxp')
      .then(({ data }) => { if (vigente) setFacturas(data); })
      .catch(() => { if (vigente) setFacturas([]); });
    api.get('/reportes/flujo-proyectado')
      .then(({ data }) => { if (vigente) setProyectado(data); })
      .catch(() => { if (vigente) setProyectado(null); });
    return () => { vigente = false; };
  }, [empresaActiva]);

  const hoy = hoyCaracas();
  const alertas = [];

  if (!(Number(tasasHoy?.tasa_bcv) > 0)) {
    alertas.push({ clave: 'tasa', nivel: 'alta', texto: 'Falta cargar la tasa BCV de hoy', ir: '/tasas-cambio' });
  }

  const vencidas = facturas.filter((f) => Number(f.dias_vencido) > 0);
  if (vencidas.length) {
    alertas.push({
      clave: 'vencidas',
      nivel: 'alta',
      texto: `${vencidas.length} factura${vencidas.length === 1 ? '' : 's'} de compra vencida${vencidas.length === 1 ? '' : 's'}`,
      ir: '/reportes/cuentas-por-pagar'
    });
  }

  const atrasadas = facturas.filter((f) => f.fecha_pago_prevista && String(f.fecha_pago_prevista).slice(0, 10) < hoy);
  if (atrasadas.length) {
    alertas.push({
      clave: 'agenda',
      nivel: 'media',
      texto: `${atrasadas.length} pago${atrasadas.length === 1 ? '' : 's'} de la agenda con fecha pasada sin pagar`,
      ir: '/reportes/cuentas-por-pagar'
    });
  }

  // #86: el saldo proyectado (cobros y pagos esperados) cae bajo la suma de
  // los mínimos de las cuentas en los próximos 90 días.
  const bajo = proyectado?.bajo_minimo?.bcv;
  if (bajo) {
    alertas.push({
      clave: 'proyectado',
      nivel: bajo.dias <= 15 ? 'alta' : 'media',
      texto: bajo.dias > 0
        ? `El saldo proyectado cae bajo el mínimo el ${formatFecha(bajo.fecha)} (en ${bajo.dias} día${bajo.dias === 1 ? '' : 's'})`
        : 'Con los pagos vencidos, el saldo ya queda bajo el mínimo',
      ir: '/reportes/flujo-proyectado'
    });
  }

  const activas = cuentas.filter((c) => Number(c.activo) === 1);
  activas
    .filter((c) => c.saldo_minimo !== null && c.saldo_minimo !== undefined && Number(c.saldo_actual) < Number(c.saldo_minimo))
    .forEach((c) => alertas.push({
      clave: `minimo-${c.id}`,
      nivel: 'alta',
      texto: `${c.nombre} por debajo de su mínimo (${c.moneda === 'BS' ? 'Bs ' : '$'}${formatMonto(c.saldo_actual)})`,
      ir: `/movimientos?cuenta_id=${c.id}`
    }));

  activas
    .filter((c) => c.ultima_fecha && diasDesde(c.ultima_fecha, hoy) > DIAS_SIN_MOVIMIENTO)
    .forEach((c) => alertas.push({
      clave: `quieta-${c.id}`,
      nivel: 'baja',
      texto: `${c.nombre} sin movimientos desde el ${formatFecha(c.ultima_fecha)}`,
      ir: `/movimientos?cuenta_id=${c.id}`
    }));

  const colorNivel = { alta: 'text-red-600', media: 'text-amber-600', baja: 'text-slate-400' };

  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 flex flex-col ${altoLibre ? 'max-h-[274px]' : 'h-[274px]'}`}>
      <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
        Alertas
        {alertas.length > 0 && (
          <span className="text-[11px] font-medium bg-red-50 text-red-700 rounded-full px-1.5">{alertas.length}</span>
        )}
      </h2>
      {alertas.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm gap-1">
          <CheckCircle2 size={22} className="text-green-500" />
          Nada pendiente
        </div>
      ) : (
        <ul className="mt-2 flex-1 overflow-y-auto -mx-1">
          {alertas.map((a) => (
            <li key={a.clave}>
              <button
                type="button"
                onClick={() => navigate(a.ir)}
                className="w-full flex items-start gap-2 text-left text-xs px-1 py-1.5 rounded hover:bg-slate-50"
              >
                <AlertTriangle size={14} className={`shrink-0 mt-px ${colorNivel[a.nivel]}`} />
                <span className="flex-1 text-slate-600">{a.texto}</span>
                <ChevronRight size={14} className="shrink-0 text-slate-300" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
